import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  ShieldCheckIcon, 
  EnvelopeIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { initialScoreConfigRequest, scoreConfigRequest, scoreConfigResponse } from '../Types/AdminType';
import { createScoreConfig, deleteScoreConfig, updateScoreConfig } from '../Services/AdminService';
import { DropdownSingle } from '../../Shared/Component/forms/DropdownSingle';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';

// 1. UPDATED INTERFACE
// Added necessary fields so TypeScript recognizes the properties used in your JSX
interface Recruiter {
  id: string;
  fieldName: string;
  scoreType: string;
  weight: number;
  enabled: boolean;
  fieldType: string;
}

interface Props {
  data: scoreConfigResponse[];
  onEditSubmit: (data: Recruiter) => void;
  onDeleteConfirm: (email: string) => void;
  onRegisterSubmit: (data: Partial<Recruiter>) => void; 
  menuName: string;
}

const ScoreConfigComponent = ({ data, onEditSubmit, onDeleteConfirm, onRegisterSubmit, menuName }: Props) => {
  const [selectedRecruiter, setSelectedRecruiter] = useState<Recruiter | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

    const [adminData, setAdminData] = useState<scoreConfigRequest | null>(initialScoreConfigRequest);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [alert, setAlert] = useState<AlertProps | null>(null);

    const redirect = useRedirect();

   const {
      values,
      setAllValues,
      handleInputChange,
      handleRequiredValueChange,
      handleValueChange,
      handleRequiredValueChangeForDD,
    } = useFormController<scoreConfigRequest>(initialScoreConfigRequest);

  const [newRecruiter, setNewRecruiter] = useState<Partial<Recruiter>>({
  fieldName: "",
  scoreType: "",
  weight: 0,
  enabled: false,
  fieldType: "",
  });

  // Casting as Recruiter here ensures compatibility with the map data
  const handleEditClick = (recruiter: any) => {
     setSelectedRecruiter(recruiter as Recruiter);
  // ✅ IMPORTANT: sync into form state
  setAllValues({
    fieldName: recruiter.fieldName || "",
    fieldType: recruiter.fieldType || "",
    scoreType: recruiter.scoreType || "",
    weight: recruiter.weight || 0,
    enabled: recruiter.enabled || false,
  });

  setIsEditOpen(true);
  };

  const handleDeleteClick = (recruiter: any) => {
    setSelectedRecruiter(recruiter as Recruiter);
    setIsDeleteOpen(true);
    
  };


  

  
   const handleCreateSubmit = async () => {
    setAlert(null);
    const response = await createScoreConfig(values);
  
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.data.fieldName + " has been added successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }


  const handleEditSubmit = async () => {
    setAlert(null);
    
    const response = await updateScoreConfig(values, selectedRecruiter?.id || "");
  
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.data.fieldName + " has been updated successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }


    const handleDeleteSubmit = async () => {
    setAlert(null);
    const response = await deleteScoreConfig(selectedRecruiter?.id || "");
    
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message:  selectedRecruiter?.fieldName + " has been deleted successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Delete Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end px-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">{menuName} Directory</h2>
          <p className="text-sm text-slate-500 font-medium">Manage and monitor team access levels</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05, translateY: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsRegisterOpen(true)}
          className="flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white rounded-2xl font-bold text-sm shadow-xl shadow-indigo-200 border border-indigo-400/20"
        >
          <UserPlusIcon className="w-5 h-5" />
          Add {menuName}
        </motion.button>
      </div>

       {alert && (
                  <div className="mb-6">
                    <Alert {...alert} onClose={() => setAlert(null)} />
                  </div>
                )}

      <div className="w-full p-1 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-[2.5rem]">
      <div className="overflow-x-auto overflow-hidden bg-white/70 backdrop-blur-2xl rounded-[2.4rem] border border-white shadow-2xl">
      <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Field Name</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Field Type</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Score Type</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">weight</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Status</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Mapping</th>

                <th className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Role & Access</th>
                {/* <th className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Created Date</th> */}
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/50">
              {data.map((recruiter: any, idx) => (
                <motion.tr 
                  key={recruiter.targetRole}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group hover:bg-white transition-all duration-300"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.fieldName || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                   <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.fieldType || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                   <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.scoreType || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                   <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.weight || "?"}
                        </div>
                      </div>
                    </div>
                  </td>

                <td>
                    <div
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all
      ${
        recruiter.enabled
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-rose-50 text-rose-700 border-rose-200"
      }`}
  >
    {/* Dot Indicator */}
    <span
      className={`w-2 h-2 rounded-full ${
        recruiter.enabled ? "bg-emerald-500" : "bg-rose-500"
      }`}
    ></span>

    {recruiter.enabled ? "Active" : "Inactive"}
  </div>
</td>

<td className="px-8 py-5 text-right">
  <div className="flex justify-end items-center gap-3">
    <button 
      onClick={() => redirect(`/admin/candidate-score-mapping-config/${encodeURIComponent(recruiter.fieldName)}`)}
      className="group relative p-[1px] rounded-xl transition-all active:scale-95"
    >
      {/* Animated Gradient Background on Hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl opacity-20 group-hover:opacity-100 transition-opacity" />
      
      {/* Button Content */}
      <div className="relative px-4 py-2 bg-white dark:bg-zinc-900 rounded-[11px] text-xs font-bold text-indigo-600 dark:text-indigo-300 group-hover:text-indigo-900 dark:group-hover:text-white transition-colors flex items-center gap-2">
        <span className="truncate">Map Value</span>
        {/* Optional: Add a small icon for visual power */}
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </div>
    </button>
  </div>
</td>
                   
                  <td className="px-6 py-5">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <BriefcaseIcon className="w-4 h-4 text-indigo-400" />
                        Admin
                      </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[10px] font-black uppercase border border-amber-100">
                          <ShieldCheckIcon className="w-3 h-3" /> System Admin
                        </span>
                    </div>
                  </td>
                
                  {/* <td className="px-6 py-5">
                    <div className="flex items-center gap-2 text-sm text-slate-500 font-semibold">
                      <CalendarDaysIcon className="w-4 h-4 text-slate-300" />
                      {recruiter.createdAt ? new Date(recruiter.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "N/A"}
                    </div>
                  </td> */}
                  <td className="px-8 py-5 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleEditClick(recruiter)}
                        className="p-2.5 bg-white text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl shadow-sm border border-indigo-50 transition-all active:scale-95"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(recruiter)}
                        className="p-2.5 bg-white text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl shadow-sm border border-rose-50 transition-all active:scale-95"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- REGISTER MODAL --- */}
      <Transition show={isRegisterOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsRegisterOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-8" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-8">
                <Dialog.Panel className="w-full max-w-xl bg-white rounded-[3rem] p-10 shadow-2xl border border-slate-100 relative">
                  <div className="flex justify-between items-center mb-8">
                    <div className="space-y-1">
                      <Dialog.Title className="text-3xl font-black text-slate-800 tracking-tight">Create New {menuName}</Dialog.Title>
                      <p className="text-sm text-slate-500 font-medium">Add a new {menuName} category to your Job Portal</p>
                    </div>
                    <button onClick={() => setIsRegisterOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                      <XMarkIcon className="w-6 h-6 text-slate-400" />
                    </button>
                  </div>

                  <div className="space-y-5">
                    <TextInput
                      label="Field Name"
                      name="fieldName"
                      placeholder="Enter the fieldName"
                      value={values.fieldName}
                      onChange={handleInputChange("fieldName")}
                    />

                     <TextInput
                      label="Field Type"
                      name="fieldType"
                      placeholder="Enter the fieldType"
                      value={values.fieldType}
                      onChange={handleInputChange("fieldType")}
                    />

                    <TextInput
                      label="Score Type"
                      name="scoreType"
                      placeholder="Enter the scoreType"
                      value={values.scoreType}
                      onChange={handleInputChange("scoreType")}
                    />

                     <TextInput
                      label="Weight (Numbers)"
                      name="weight"
                      placeholder="Enter the weight"
                      value={values.weight.toString()}
                      onChange={handleInputChange("weight")}
                    />

                    <DropdownSingle<boolean> // Change type to boolean
                      label="Plan Status"
                      options={[
                        { label: "Active", value: true },  // Use actual boolean
                        { label: "No", value: false },    // Use actual boolean
                      ]}
                      value={values.enabled}               // Pass boolean directly
                      onChange={(val) => {
                        // val will be true, false, or null (if cleared)
                        handleRequiredValueChange("enabled")(val);
                      }}
                      placeholder="Select plan status"
                    />

                    <button 
                      onClick={() => { handleCreateSubmit(); onRegisterSubmit(newRecruiter); setIsRegisterOpen(false); }}
                      className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all transform hover:-translate-y-1 active:scale-[0.98] mt-4"
                    >
                      Create {menuName}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* --- EDIT MODAL --- */}
      <Transition show={isEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsEditOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-[3rem] p-10 shadow-2xl">
                  <div className="flex justify-between items-center mb-8">
                    <Dialog.Title className="text-3xl font-black text-slate-800">Edit {selectedRecruiter?.fieldName} {menuName}</Dialog.Title>
                    <button onClick={() => setIsEditOpen(false)}><XMarkIcon className="w-6 h-6 text-slate-400" /></button>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-1">
                   
                       <TextInput
                      label="Field Name"
                      name="fieldName"
                      placeholder="Enter the fieldName"
                      value={values.fieldName }
                      onChange={handleInputChange("fieldName")}
                    />

                     <TextInput
                      label="Field Type"
                      name="fieldType"
                      placeholder="Enter the fieldType"
                      value={values.fieldType}
                      onChange={handleInputChange("fieldType")}
                    />

                    <TextInput
                      label="Score Type"
                      name="scoreType"
                      placeholder="Enter the scoreType"
                      value={values.scoreType}
                      onChange={handleInputChange("scoreType")}
                    />

                     <TextInput
                      label="Weight (Numbers)"
                      name="weight"
                      placeholder="Enter the weight"
                      value={values.weight.toString()}
                      onChange={handleInputChange("weight")}
                    />

                    <DropdownSingle<boolean> // Change type to boolean
                      label="Config Status"
                      options={[
                        { label: "Active", value: true },  // Use actual boolean
                        { label: "No", value: false },    // Use actual boolean
                      ]}
                      value={values.enabled}               // Pass boolean directly
                      onChange={(val) => {
                        // val will be true, false, or null (if cleared)
                        handleRequiredValueChange("enabled")(val);
                      }}
                      placeholder="Select plan status"
                    />
                    
                    </div>
                    <button onClick={() => { handleEditSubmit(); onEditSubmit(selectedRecruiter!); setIsEditOpen(false); }} className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl">Update {menuName}</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>







      {/* --- DELETE MODAL --- */}
      <Transition show={isDeleteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md bg-white rounded-[2.5rem] p-8 text-center shadow-2xl">
                  <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-500">
                    <ExclamationTriangleIcon className="w-10 h-10" />
                  </div>
                  <Dialog.Title className="text-2xl font-black text-slate-800 mb-2">Confirm Removal</Dialog.Title>
                  <p className="text-sm text-slate-500 font-medium mb-8 px-4">
                    Are you sure you want to delete <span className="text-slate-800 font-bold">{selectedRecruiter?.fieldName}</span>?
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setIsDeleteOpen(false)} className="flex-1 py-4 bg-slate-100 text-slate-600 font-bold rounded-2xl">Cancel</button>
                    <button onClick={() => { handleDeleteSubmit(); if(selectedRecruiter) onDeleteConfirm(selectedRecruiter.fieldName); setIsDeleteOpen(false); }} className="flex-1 py-4 bg-rose-500 text-white font-bold rounded-2xl">Delete</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

const InputField = ({ label, placeholder, onChange }: any) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black uppercase text-slate-400 ml-1">{label}</label>
    <input 
      type="text" 
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-slate-300 placeholder:font-medium" 
    />
  </div>
);

export default ScoreConfigComponent;